import { Check, Minus } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { packages, comparisonRows, fmtPrice } from '../../data/webServicesData';
import AnimateIn from '../AnimateIn';

const Cell = ({ value, color, language }) => {
  if (value === true) return <Check size={16} strokeWidth={2.5} className="mx-auto" style={{ color }} />;
  if (value === false) return <Minus size={15} className="mx-auto" style={{ color: 'rgba(255,255,255,0.15)' }} />;
  const text = typeof value === 'object' ? value[language] : value;
  return <span className="text-[13px] font-semibold" style={{ color }}>{text}</span>;
};

const ComparisonTable = () => {
  const { language, t } = useLanguage();

  return (
    <section id="ws-compare" className="py-20 md:py-28 px-6 lg:px-8 scroll-mt-20" data-testid="comparison-section">
      <div className="max-w-[1200px] mx-auto">
        <AnimateIn className="text-center mb-14">
          <p className="accent-tag justify-center">{t('wsCompareTag')}</p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white mb-4"
            style={{ fontFamily: "'Syne', sans-serif" }}
            data-testid="comparison-title"
          >
            {t('wsCompareTitle')}
          </h2>
          <p className="text-base max-w-xl mx-auto text-gray-400">
            {t('wsCompareSubtitle')}
          </p>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="overflow-x-auto rounded-2xl border border-white/10" style={{ background: '#0b0b0b' }}>
            <table className="w-full min-w-[780px]" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-6 px-6 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    {t('wsCompareFeature')}
                  </th>
                  {packages.map((p) => (
                    <th
                      key={p.id}
                      className="py-5 px-4 text-center min-w-[132px] relative"
                      style={{ background: `${p.color}${p.popular ? '14' : '08'}` }}
                    >
                      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }} />
                      <p className="text-sm font-bold" style={{ fontFamily: "'Syne', sans-serif", color: p.color }}>
                        {p.name[language]}
                      </p>
                      <p className="text-xs font-semibold mt-1 text-gray-300">
                        {p.startingAt && `${t('wsStartingAt')} `}
                        {fmtPrice(p.price, language)}
                      </p>
                      {p.popular && (
                        <span
                          className="inline-block mt-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-[0.12em] uppercase text-black"
                          style={{ background: p.color }}
                        >
                          {t('wsMostPopular')}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-white/[0.05] last:border-0 transition-colors hover:bg-white/[0.04]"
                    data-testid={`comparison-row-${i}`}
                  >
                    <td className="py-3.5 px-6 text-[13px] text-gray-400">
                      {row.label[language]}
                    </td>
                    {row.values.map((v, vi) => (
                      <td
                        key={vi}
                        className="py-3.5 px-4 text-center"
                        style={{ background: `${packages[vi].color}${packages[vi].popular ? '0F' : '06'}` }}
                      >
                        <Cell value={v} color={packages[vi].color} language={language} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnimateIn>
      </div>
    </section>
  );
};

export default ComparisonTable;
